import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Calendar, CheckCircle, AlertCircle, Filter, X, Search, Zap, Loader2, UserCheck, Upload, FileDown, BarChart3, Info, Shield, Sparkles } from 'lucide-react';
import { formatDateLong, formatDateShort } from '@/utils/dateFormat';
import { DateBucketedList } from '@/components/DateBucketedList';
import { MoreOptionsMenu } from '@/components/common/MoreOptionsMenu';

const ExamInvigilationPage = () => {
  const { token, user } = useContext(AuthContext);
  const [exams, setExams] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examDialogOpen, setExamDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);

  // Filter states for Exam Schedules tab
  const [examVenueFilter, setExamVenueFilter] = useState('all');
  const [examClassFilter, setExamClassFilter] = useState('all');
  const [examDateFilter, setExamDateFilter] = useState('all');

  // Filter states for Invigilation Assignments tab
  const [assignVenueFilter, setAssignVenueFilter] = useState('all');
  const [assignTeacherFilter, setAssignTeacherFilter] = useState('all');
  const [assignDateFilter, setAssignDateFilter] = useState('all');

  // Global search
  const [searchQuery, setSearchQuery] = useState('');

  const [examFormData, setExamFormData] = useState({
    exam_name: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    venue: '',
    subject: '',
    classes: '',
    num_students: 0,
  });
  const [assignFormData, setAssignFormData] = useState({
    exam_schedule_id: '',
    invigilator_id: '',
    duty_type: 'main'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [examsRes, assignmentsRes, teachersRes] = await Promise.all([
        axios.get(`${API}/exams`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/invigilation`, { headers: { Authorization: `Bearer ${token}` } }),
        user?.role === 'admin' ? axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve({ data: [] })
      ]);
      setExams(examsRes.data);
      setAssignments(assignmentsRes.data);
      // Include teachers + HODs + Excos — these are all valid invigilator roles in the engine.
      // Previously filtered to role==='teacher' which dropped HOD/Exco picks, causing empty dropdown values.
      setTeachers(teachersRes.data.filter(u => ['teacher', 'hod', 'exco'].includes(u.role) && u.is_active));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleExamSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...examFormData,
        classes: examFormData.classes.split(',').map(c => c.trim()).filter(Boolean),
        num_students: parseInt(examFormData.num_students) || 0,
      };
      await axios.post(`${API}/exams`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Exam schedule created');
      setExamDialogOpen(false);
      resetExamForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create exam');
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invigilation`, assignFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invigilator assigned successfully');
      setAssignDialogOpen(false);
      resetAssignForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign invigilator');
    }
  };

  // ─── Smart Draft Flow ───
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [confirmingDraft, setConfirmingDraft] = useState(false);
  const [draftExamId, setDraftExamId] = useState('');

  // ─── Seed Demo Exams ───
  const [seedingExams, setSeedingExams] = useState(false);
  const handleSeedDemoExams = async () => {
    const confirmed = window.confirm(
      'This will REPLACE all existing exam schedules and invigilation assignments with a realistic MOE-aligned End-of-Year demo exam timetable.\n\n' +
      'It covers ~27 papers across 8 working days, starting ~2 weeks from today. Subjects span Lower Sec (English, Math, Science, MT, Humanities) and Upper Sec (Pure Sciences, Additional Math, Social Studies, Applied Subjects, N(T) Specialised Electives).\n\n' +
      'Continue?'
    );
    if (!confirmed) return;
    setSeedingExams(true);
    try {
      const res = await axios.post(`${API}/exams/seed-demo`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Seeded ${res.data.exams_created} exams across ${res.data.venues.length} venues and ${res.data.dates.length} dates.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to seed demo exams');
    } finally {
      setSeedingExams(false);
    }
  };

  // ─── Bulk Import ───
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkFileRef = useRef(null);

  // ─── Workload Stats ───
  const [workloadDialogOpen, setWorkloadDialogOpen] = useState(false);
  const [workloadStats, setWorkloadStats] = useState([]);
  const [workloadLoading, setWorkloadLoading] = useState(false);

  // ─── Opt-Outs ───
  const [optOutDialogOpen, setOptOutDialogOpen] = useState(false);
  const [optOutForm, setOptOutForm] = useState({
    mode: 'single',         // 'single' | 'range' | 'multi'
    date: '',               // single
    start_date: '',         // range
    end_date: '',           // range
    dates: '',              // multi (comma-separated)
    start_time: '',         // single (optional)
    end_time: '',           // single (optional)
    reason: '',
  });
  const [myOptOuts, setMyOptOuts] = useState([]);
  const [optOutReviewDialogOpen, setOptOutReviewDialogOpen] = useState(false);
  const [allOptOuts, setAllOptOuts] = useState([]);

  // ─── PDF Export ───
  const [pdfExporting, setPdfExporting] = useState(false);
  const [excelExporting, setExcelExporting] = useState(false);

  // ─── Fetch & handlers for the new features ───
  const fetchMyOptOuts = async () => {
    try {
      const res = await axios.get(`${API}/invigilation/opt-out`, { headers: { Authorization: `Bearer ${token}` } });
      setMyOptOuts(res.data);
    } catch { /* ignore */ }
  };

  const fetchAllOptOuts = async () => {
    try {
      const res = await axios.get(`${API}/invigilation/opt-out`, { headers: { Authorization: `Bearer ${token}` } });
      setAllOptOuts(res.data);
    } catch { /* ignore */ }
  };

  const fetchWorkloadStats = async () => {
    setWorkloadLoading(true);
    try {
      const res = await axios.get(`${API}/invigilation/workload-stats`, { headers: { Authorization: `Bearer ${token}` } });
      setWorkloadStats(res.data);
    } catch {
      toast.error('Failed to load workload stats');
    } finally {
      setWorkloadLoading(false);
    }
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    if (!bulkFile) { toast.error('Select a file first'); return; }
    setBulkImporting(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const res = await axios.post(`${API}/exams/bulk-import`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setBulkResult(res.data);
      toast.success(`Imported ${res.data.created} exam(s)${res.data.errors.length ? `, ${res.data.errors.length} errors` : ''}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk import failed');
    } finally {
      setBulkImporting(false);
    }
  };

  const handleExportPdf = async () => {
    setPdfExporting(true);
    try {
      const res = await axios.get(`${API}/invigilation/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invigilation_roster.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch {
      toast.error('PDF export failed');
    } finally {
      setPdfExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExcelExporting(true);
    try {
      const res = await axios.get(`${API}/invigilation/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invigilation_roster.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch {
      toast.error('Excel export failed');
    } finally {
      setExcelExporting(false);
    }
  };

  const handleSubmitOptOut = async (e) => {
    e.preventDefault();
    if (!optOutForm.reason.trim()) { toast.error('Reason is required'); return; }
    const payload = { reason: optOutForm.reason, mode: optOutForm.mode };
    if (optOutForm.mode === 'single') {
      if (!optOutForm.date) { toast.error('Pick a date'); return; }
      payload.dates = [optOutForm.date];
      if (optOutForm.start_time) payload.start_time = optOutForm.start_time;
      if (optOutForm.end_time) payload.end_time = optOutForm.end_time;
    } else if (optOutForm.mode === 'range') {
      if (!optOutForm.start_date || !optOutForm.end_date) { toast.error('Pick start and end dates'); return; }
      if (optOutForm.end_date < optOutForm.start_date) { toast.error('End date must be on or after start date'); return; }
      payload.start_date = optOutForm.start_date;
      payload.end_date = optOutForm.end_date;
    } else {
      const dates = optOutForm.dates.split(',').map(d => d.trim()).filter(Boolean);
      if (dates.length === 0) { toast.error('Add at least one date (comma-separated YYYY-MM-DD)'); return; }
      payload.dates = dates;
    }
    try {
      await axios.post(`${API}/invigilation/opt-out`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Opt-out request submitted');
      setOptOutDialogOpen(false);
      setOptOutForm({ mode: 'single', date: '', start_date: '', end_date: '', dates: '', start_time: '', end_time: '', reason: '' });
      fetchMyOptOuts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleReviewOptOut = async (optOutId, status) => {
    try {
      await axios.patch(`${API}/invigilation/opt-out/${optOutId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Opt-out ${status}`);
      fetchAllOptOuts();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleGenerateDraft = async (examId) => {
    setDraftExamId(examId);
    setDraftDialogOpen(true);
    setDraft(null);
    setDraftLoading(true);
    try {
      const res = await axios.post(`${API}/invigilation/generate-draft?exam_schedule_id=${examId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDraft(res.data);
      if (res.data.unassigned_slots > 0) {
        toast.warning(`Draft generated: ${res.data.unassigned_slots} slot(s) need manual selection`);
      } else {
        toast.success(`Draft generated: ${res.data.assigned_count} invigilator(s) suggested`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate draft');
      setDraftDialogOpen(false);
    } finally {
      setDraftLoading(false);
    }
  };

  const updateDraftSlot = (idx, invigilatorId) => {
    if (!draft) return;
    const teacher = teachers.find(t => t.id === invigilatorId);
    const newSlots = [...draft.draft_slots];
    newSlots[idx] = {
      ...newSlots[idx],
      invigilator_id: invigilatorId || null,
      invigilator_name: teacher ? teacher.name : null,
      match: newSlots[idx].match || 'any',
    };
    setDraft({ ...draft, draft_slots: newSlots });
  };

  const handleConfirmDraft = async () => {
    if (!draft) return;
    const filled = draft.draft_slots.filter(s => s.invigilator_id);
    if (filled.length === 0) {
      toast.error('Assign at least one invigilator before confirming');
      return;
    }
    setConfirmingDraft(true);
    try {
      const res = await axios.post(`${API}/invigilation/confirm-draft`, {
        exam_schedule_id: draft.exam_schedule_id,
        draft_slots: filled,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Confirmed ${res.data.count} invigilation assignment(s). Notifications sent.`);
      setDraftDialogOpen(false);
      setDraft(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm');
    } finally {
      setConfirmingDraft(false);
    }
  };

  const handleAcknowledge = async (assignmentId) => {
    try {
      await axios.patch(`${API}/invigilation/${assignmentId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Assignment acknowledged');
      fetchData();
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const resetExamForm = () => {
    setExamFormData({
      exam_name: '',
      date: new Date().toISOString().split('T')[0],
      start_time: '',
      end_time: '',
      venue: '',
      subject: '',
      classes: '',
      num_students: 0,
    });
  };

  const resetAssignForm = () => {
    setAssignFormData({
      exam_schedule_id: '',
      invigilator_id: '',
      duty_type: 'main'
    });
  };

  const isAdmin = user?.role === 'admin';

  // Derive unique filter options from exams data
  const examFilterOptions = useMemo(() => {
    const venues = [...new Set(exams.map(e => e.venue).filter(Boolean))].sort();
    const classes = [...new Set(exams.flatMap(e => e.classes || []))].sort();
    const dates = [...new Set(exams.map(e => e.date).filter(Boolean))].sort();
    return { venues, classes, dates };
  }, [exams]);

  // Derive unique filter options from assignments data
  const assignFilterOptions = useMemo(() => {
    const venues = [...new Set(assignments.map(a => a.venue).filter(Boolean))].sort();
    const teacherNames = [...new Set(assignments.map(a => a.invigilator_name).filter(Boolean))].sort();
    const dates = [...new Set(assignments.map(a => a.date).filter(Boolean))].sort();
    return { venues, teacherNames, dates };
  }, [assignments]);

  // Filtered exams
  const filteredExams = useMemo(() => {
    return exams.filter(exam => {
      if (examVenueFilter !== 'all' && exam.venue !== examVenueFilter) return false;
      if (examClassFilter !== 'all' && !(exam.classes || []).includes(examClassFilter)) return false;
      if (examDateFilter !== 'all' && exam.date !== examDateFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const match = (exam.exam_name || '').toLowerCase().includes(q) ||
          (exam.venue || '').toLowerCase().includes(q) ||
          (exam.subject || '').toLowerCase().includes(q) ||
          (exam.date || '').includes(q) ||
          (exam.classes || []).some(c => c.toLowerCase().includes(q));
        if (!match) return false;
      }
      return true;
    });
  }, [exams, examVenueFilter, examClassFilter, examDateFilter, searchQuery]);

  // Filtered assignments
  const filteredAssignments = useMemo(() => {
    return assignments.filter(a => {
      if (assignVenueFilter !== 'all' && a.venue !== assignVenueFilter) return false;
      if (assignTeacherFilter !== 'all' && a.invigilator_name !== assignTeacherFilter) return false;
      if (assignDateFilter !== 'all' && a.date !== assignDateFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const match = (a.invigilator_name || '').toLowerCase().includes(q) ||
          (a.venue || '').toLowerCase().includes(q) ||
          (a.exam_name || '').toLowerCase().includes(q) ||
          (a.date || '').includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [assignments, assignVenueFilter, assignTeacherFilter, assignDateFilter, searchQuery]);

  const examFiltersActive = examVenueFilter !== 'all' || examClassFilter !== 'all' || examDateFilter !== 'all';
  const assignFiltersActive = assignVenueFilter !== 'all' || assignTeacherFilter !== 'all' || assignDateFilter !== 'all';

  // Group exams by date with newly created section
  // Count invigilator assignments per exam_schedule_id (for the status badge)
  const assignmentsByExam = useMemo(() => {
    const map = {};
    assignments.forEach(a => {
      if (a.exam_schedule_id) {
        const bucket = map[a.exam_schedule_id] || { total: 0, acknowledged: 0 };
        bucket.total += 1;
        if (a.is_acknowledged) bucket.acknowledged += 1;
        map[a.exam_schedule_id] = bucket;
      }
    });
    return map;
  }, [assignments]);

  const renderAssignmentBadge = (examId) => {
    const stats = assignmentsByExam[examId] || { total: 0, acknowledged: 0 };
    const { total, acknowledged } = stats;

    if (total === 0) {
      return (
        <span
          className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded inline-flex items-center gap-1"
          data-testid="exam-unassigned-badge"
        >
          <AlertCircle className="h-3 w-3" /> Unassigned
        </span>
      );
    }

    if (acknowledged === total) {
      return (
        <span
          className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-medium rounded inline-flex items-center gap-1"
          data-testid="exam-all-acknowledged-badge"
        >
          <CheckCircle className="h-3 w-3" /> All {total} acknowledged
        </span>
      );
    }

    if (acknowledged > 0) {
      return (
        <span
          className="px-2 py-0.5 bg-teal-100 text-teal-800 text-xs font-medium rounded inline-flex items-center gap-1"
          data-testid="exam-partially-acknowledged-badge"
        >
          <CheckCircle className="h-3 w-3" />
          {acknowledged}/{total} acknowledged
        </span>
      );
    }

    return (
      <span
        className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded inline-flex items-center gap-1"
        data-testid="exam-assigned-badge"
      >
        <CheckCircle className="h-3 w-3" /> {total} invigilator{total === 1 ? '' : 's'} assigned
      </span>
    );
  };

  const { newlyCreatedExams, restExams } = useMemo(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const sorted = [...filteredExams].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const recent = [];
    const rest = [];
    sorted.forEach(e => {
      if (e.created_at && e.created_at >= oneDayAgo) recent.push(e);
      else rest.push(e);
    });
    return { newlyCreatedExams: recent, restExams: rest };
  }, [filteredExams]);

  const { newlyAssignedInvig, restInvig } = useMemo(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const sorted = [...filteredAssignments].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const recent = [];
    const rest = [];
    sorted.forEach(a => {
      if (a.created_at && a.created_at >= oneDayAgo) recent.push(a);
      else rest.push(a);
    });
    return { newlyAssignedInvig: recent, restInvig: rest };
  }, [filteredAssignments]);

  const clearExamFilters = () => {
    setExamVenueFilter('all');
    setExamClassFilter('all');
    setExamDateFilter('all');
  };

  const clearAssignFilters = () => {
    setAssignVenueFilter('all');
    setAssignTeacherFilter('all');
    setAssignDateFilter('all');
  };

  return (
    <div data-testid="invigilation-page">
      {/* Header: title stacks above the toolbar on mobile so the heading
          isn't squeezed into a tiny side column. On md+ the toolbar floats
          to the right of the title as before. */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">Exam Invigilation</h1>
          <p className="text-base text-slate-500 mt-2">Manage exam schedules and invigilation assignments</p>
        </div>
        {!isAdmin && (
          <Button variant="outline" className="border-slate-300 w-full md:w-auto" onClick={() => { setOptOutDialogOpen(true); fetchMyOptOuts(); }} data-testid="request-optout-button">
            <Shield className="mr-2 h-4 w-4" /> Request Opt-Out
          </Button>
        )}
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {/* Secondary actions — visible on desktop only. */}
            <div className="hidden md:flex gap-2 flex-wrap">
              <Button variant="outline" className="border-slate-300" onClick={() => { setWorkloadDialogOpen(true); fetchWorkloadStats(); }} data-testid="workload-stats-button">
                <BarChart3 className="mr-2 h-4 w-4" /> Workload
              </Button>
              <Button variant="outline" className="border-slate-300" onClick={() => { setOptOutReviewDialogOpen(true); fetchAllOptOuts(); }} data-testid="optout-review-button">
                <Shield className="mr-2 h-4 w-4" /> Opt-Outs
              </Button>
              <Button variant="outline" className="border-slate-300" onClick={() => setBulkDialogOpen(true)} data-testid="bulk-import-button">
                <Upload className="mr-2 h-4 w-4" /> Bulk Import
              </Button>
              <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={handleSeedDemoExams} disabled={seedingExams} data-testid="seed-demo-exams-button">
                <Sparkles className="mr-2 h-4 w-4" /> {seedingExams ? 'Seeding...' : 'Seed Demo Exams'}
              </Button>
              <Button variant="outline" className="border-slate-300" onClick={handleExportPdf} disabled={pdfExporting} data-testid="export-pdf-button">
                <FileDown className="mr-2 h-4 w-4" /> {pdfExporting ? 'Exporting...' : 'Export PDF'}
              </Button>
              <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={handleExportExcel} disabled={excelExporting} data-testid="export-excel-button">
                <FileDown className="mr-2 h-4 w-4" /> {excelExporting ? 'Exporting...' : 'Export Excel'}
              </Button>
            </div>

            {/* Mobile: secondary actions collapsed via the shared
                MoreOptionsMenu so the header stays clean on small screens. */}
            <MoreOptionsMenu
              className="md:hidden"
              testid="exam-more-actions-button"
              groups={[
                {
                  label: 'Reports',
                  items: [
                    { label: 'Workload', icon: <BarChart3 className="h-4 w-4" />, onClick: () => { setWorkloadDialogOpen(true); fetchWorkloadStats(); }, testid: 'mobile-workload-stats-button' },
                    { label: pdfExporting ? 'Exporting…' : 'Export PDF', icon: <FileDown className="h-4 w-4" />, onClick: handleExportPdf, disabled: pdfExporting, testid: 'mobile-export-pdf-button' },
                    { label: excelExporting ? 'Exporting…' : 'Export Excel', icon: <FileDown className="h-4 w-4" />, onClick: handleExportExcel, disabled: excelExporting, testid: 'mobile-export-excel-button' },
                  ],
                },
                {
                  label: 'Manage',
                  items: [
                    { label: 'Opt-Outs', icon: <Shield className="h-4 w-4" />, onClick: () => { setOptOutReviewDialogOpen(true); fetchAllOptOuts(); }, testid: 'mobile-optout-review-button' },
                    { label: 'Bulk Import', icon: <Upload className="h-4 w-4" />, onClick: () => setBulkDialogOpen(true), testid: 'mobile-bulk-import-button' },
                    { label: seedingExams ? 'Seeding…' : 'Seed Demo Exams', icon: <Sparkles className="h-4 w-4" />, onClick: handleSeedDemoExams, disabled: seedingExams, className: 'text-emerald-700', testid: 'mobile-seed-demo-exams-button' },
                  ],
                },
              ]}
            />

            {/* Primary CTAs — visible on every breakpoint (Dialogs render in
                place; on mobile the next two flex full-width). */}
            <Dialog open={examDialogOpen} onOpenChange={setExamDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-900 hover:bg-blue-800 flex-1 md:flex-initial" data-testid="create-exam-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Exam
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Exam Schedule</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleExamSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="exam-name">Exam Name</Label>
                    <Input
                      id="exam-name"
                      value={examFormData.exam_name}
                      onChange={(e) => setExamFormData({ ...examFormData, exam_name: e.target.value })}
                      placeholder="Mid-Year Examination 2024"
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={examFormData.date}
                      onChange={(e) => setExamFormData({ ...examFormData, date: e.target.value })}
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start-time">Start Time</Label>
                      <Input
                        id="start-time"
                        type="time"
                        value={examFormData.start_time}
                        onChange={(e) => setExamFormData({ ...examFormData, start_time: e.target.value })}
                        required
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end-time">End Time</Label>
                      <Input
                        id="end-time"
                        type="time"
                        value={examFormData.end_time}
                        onChange={(e) => setExamFormData({ ...examFormData, end_time: e.target.value })}
                        required
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="venue">Venue</Label>
                    <Input
                      id="venue"
                      value={examFormData.venue}
                      onChange={(e) => setExamFormData({ ...examFormData, venue: e.target.value })}
                      placeholder="Hall A, Room 301"
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={examFormData.subject}
                      onChange={(e) => setExamFormData({ ...examFormData, subject: e.target.value })}
                      placeholder="Mathematics"
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="classes">Classes (comma-separated)</Label>
                    <Input
                      id="classes"
                      value={examFormData.classes}
                      onChange={(e) => setExamFormData({ ...examFormData, classes: e.target.value })}
                      placeholder="3A, 3B, 3C"
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="num-students">Number of Students (for capacity rule)</Label>
                    <Input
                      id="num-students"
                      type="number"
                      min="0"
                      value={examFormData.num_students}
                      onChange={(e) => setExamFormData({ ...examFormData, num_students: e.target.value })}
                      placeholder="60"
                      className="mt-1.5"
                      data-testid="exam-num-students"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Used by the capacity rule (1 invigilator per N students).</p>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setExamDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-blue-900 hover:bg-blue-800">Create Exam</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex-1 md:flex-initial" data-testid="assign-invigilator-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Assign Invigilator
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Assign Invigilator</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAssignSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label>Exam</Label>
                    <Select value={assignFormData.exam_schedule_id} onValueChange={(value) => setAssignFormData({ ...assignFormData, exam_schedule_id: value })}>
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select exam" />
                      </SelectTrigger>
                      <SelectContent>
                        {exams.map(exam => (
                          <SelectItem key={exam.id} value={exam.id}>
                            {exam.exam_name} — {formatDateShort(exam.date)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Invigilator</Label>
                    <Select value={assignFormData.invigilator_id} onValueChange={(value) => setAssignFormData({ ...assignFormData, invigilator_id: value })}>
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select teacher" />
                      </SelectTrigger>
                      <SelectContent>
                        {teachers.map(teacher => (
                          <SelectItem key={teacher.id} value={teacher.id}>
                            {teacher.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Duty Type</Label>
                    <Select value={assignFormData.duty_type} onValueChange={(value) => setAssignFormData({ ...assignFormData, duty_type: value })}>
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="main">Main Invigilator</SelectItem>
                        <SelectItem value="standby">Standby</SelectItem>
                        <SelectItem value="roaming">Roaming</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-blue-900 hover:bg-blue-800">Assign</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <div className="relative mb-4" data-testid="exam-search">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by exam name, venue, teacher, subject, or date..." className="pl-9" data-testid="exam-search-input" />
      </div>

      <Tabs defaultValue="exams" className="space-y-6">
        <TabsList>
          <TabsTrigger value="exams">Exam Schedules</TabsTrigger>
          <TabsTrigger value="assignments">Invigilation Assignments</TabsTrigger>
        </TabsList>

        <TabsContent value="exams" className="space-y-4">
          {/* Exam Schedule Filters */}
          <Card className="p-4 border border-slate-200 bg-slate-50/50" data-testid="exam-filters">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Filter className="h-4 w-4" />
                Filter Schedules
              </div>
              {examFiltersActive && (
                <Button variant="ghost" size="sm" onClick={clearExamFilters} className="text-xs text-slate-500 hover:text-slate-800 h-7 px-2" data-testid="clear-exam-filters">
                  <X className="h-3 w-3 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Venue</Label>
                <Select value={examVenueFilter} onValueChange={setExamVenueFilter}>
                  <SelectTrigger className="h-9 bg-white" data-testid="exam-venue-filter">
                    <SelectValue placeholder="All Venues" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Venues</SelectItem>
                    {examFilterOptions.venues.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Class</Label>
                <Select value={examClassFilter} onValueChange={setExamClassFilter}>
                  <SelectTrigger className="h-9 bg-white" data-testid="exam-class-filter">
                    <SelectValue placeholder="All Classes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    {examFilterOptions.classes.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Date</Label>
                <Select value={examDateFilter} onValueChange={setExamDateFilter}>
                  <SelectTrigger className="h-9 bg-white" data-testid="exam-date-filter">
                    <SelectValue placeholder="All Dates" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Dates</SelectItem>
                    {examFilterOptions.dates.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {examFiltersActive && (
              <p className="text-xs text-slate-500 mt-2" data-testid="exam-filter-count">
                Showing {filteredExams.length} of {exams.length} exam schedule{exams.length !== 1 ? 's' : ''}
              </p>
            )}
          </Card>

          {loading ? (
            <div>Loading exams...</div>
          ) : filteredExams.length === 0 ? (
            <Card className="p-8 text-center">
              <Calendar className="mx-auto h-12 w-12 text-slate-300 mb-4" />
              <p className="text-slate-500">
                {examFiltersActive ? 'No exam schedules match the selected filters.' : 'No exam schedules created yet.'}
              </p>
              {examFiltersActive && (
                <Button variant="link" onClick={clearExamFilters} className="mt-2 text-sm text-slate-500">Clear filters</Button>
              )}
            </Card>
          ) : (
            <div className="space-y-6">
            {newlyCreatedExams.length > 0 && (
              <div data-testid="newly-created-exams">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">Newly Created (Last 24 Hours)</h3>
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">{newlyCreatedExams.length}</span>
                </div>
                <div className="space-y-3">
                {newlyCreatedExams.map((exam) => (
                  <Card key={exam.id} className="dashboard-card p-6 border-l-4 border-l-emerald-400" data-testid="exam-card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-slate-900">{exam.exam_name}</h3>
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">New</span>
                          {renderAssignmentBadge(exam.id)}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 text-sm text-slate-600">
                          <p><strong>Date:</strong> {formatDateLong(exam.date)}</p>
                          <p><strong>Time:</strong> {exam.start_time} - {exam.end_time}</p>
                          <p><strong>Venue:</strong> {exam.venue}</p>
                          <p><strong>Subject:</strong> {exam.subject}</p>
                          <p><strong>Classes:</strong> {exam.classes.join(', ')}</p>
                        </div>
                      </div>
                      {isAdmin && (<Button onClick={() => handleGenerateDraft(exam.id)} size="sm" variant="outline" className="ml-4 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="smart-draft-button"><Zap className="mr-1.5 h-3.5 w-3.5" />Smart Assign</Button>)}
                    </div>
                  </Card>
                ))}
                </div>
              </div>
            )}
            <DateBucketedList
              items={restExams}
              getDate={(e) => e.date}
              testidPrefix="exam-bucket"
              emptyLabel="No older exams."
              renderItem={(exam) => (
              <Card key={exam.id} className="dashboard-card p-6" data-testid="exam-card">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-slate-900">{exam.exam_name}</h3>
                      {renderAssignmentBadge(exam.id)}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 text-sm text-slate-600">
                      <p><strong>Date:</strong> {exam.date}</p>
                      <p><strong>Time:</strong> {exam.start_time} - {exam.end_time}</p>
                      <p><strong>Venue:</strong> {exam.venue}</p>
                      <p><strong>Subject:</strong> {exam.subject}</p>
                      <p><strong>Classes:</strong> {exam.classes.join(', ')}</p>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button onClick={() => handleGenerateDraft(exam.id)} size="sm" variant="outline" className="ml-4 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="smart-draft-button-alt"><Zap className="mr-1.5 h-3.5 w-3.5" />Smart Assign</Button>
                  )}
                </div>
              </Card>
              )}
            />
            </div>
          )}
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4">
          {/* Assignment Filters */}
          <Card className="p-4 border border-slate-200 bg-slate-50/50" data-testid="assignment-filters">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Filter className="h-4 w-4" />
                Filter Assignments
              </div>
              {assignFiltersActive && (
                <Button variant="ghost" size="sm" onClick={clearAssignFilters} className="text-xs text-slate-500 hover:text-slate-800 h-7 px-2" data-testid="clear-assignment-filters">
                  <X className="h-3 w-3 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Venue</Label>
                <Select value={assignVenueFilter} onValueChange={setAssignVenueFilter}>
                  <SelectTrigger className="h-9 bg-white" data-testid="assign-venue-filter">
                    <SelectValue placeholder="All Venues" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Venues</SelectItem>
                    {assignFilterOptions.venues.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Teacher</Label>
                <Select value={assignTeacherFilter} onValueChange={setAssignTeacherFilter}>
                  <SelectTrigger className="h-9 bg-white" data-testid="assign-teacher-filter">
                    <SelectValue placeholder="All Teachers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teachers</SelectItem>
                    {assignFilterOptions.teacherNames.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Date</Label>
                <Select value={assignDateFilter} onValueChange={setAssignDateFilter}>
                  <SelectTrigger className="h-9 bg-white" data-testid="assign-date-filter">
                    <SelectValue placeholder="All Dates" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Dates</SelectItem>
                    {assignFilterOptions.dates.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {assignFiltersActive && (
              <p className="text-xs text-slate-500 mt-2" data-testid="assign-filter-count">
                Showing {filteredAssignments.length} of {assignments.length} assignment{assignments.length !== 1 ? 's' : ''}
              </p>
            )}
          </Card>

          {loading ? (
            <div>Loading assignments...</div>
          ) : filteredAssignments.length === 0 ? (
            <Card className="p-8 text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-slate-300 mb-4" />
              <p className="text-slate-500">
                {assignFiltersActive ? 'No assignments match the selected filters.' : 'No invigilation assignments yet.'}
              </p>
              {assignFiltersActive && (
                <Button variant="link" onClick={clearAssignFilters} className="mt-2 text-sm text-slate-500">Clear filters</Button>
              )}
            </Card>
          ) : (
            <div className="space-y-6">
            {newlyAssignedInvig.length > 0 && (
              <div data-testid="newly-assigned-invig">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">Newly Assigned (Last 24 Hours)</h3>
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">{newlyAssignedInvig.length}</span>
                </div>
                <div className="space-y-3">
                {newlyAssignedInvig.map((assignment) => (
                  <Card key={assignment.id} className="dashboard-card p-6 border-l-4 border-l-emerald-400" data-testid="assignment-card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-slate-900">{assignment.invigilator_name}</h3>
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">New</span>
                          {assignment.is_acknowledged ? (<span className="flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded"><CheckCircle className="mr-1 h-3 w-3" />Acknowledged</span>) : (<span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded">Pending</span>)}
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-900 text-xs font-medium rounded">{assignment.duty_type}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 text-sm text-slate-600">
                          <p><strong>Date:</strong> {formatDateLong(assignment.date)}</p>
                          <p><strong>Time:</strong> {assignment.time_slot}</p>
                          <p><strong>Venue:</strong> {assignment.venue}</p>
                        </div>
                      </div>
                      {!assignment.is_acknowledged && user?.id === assignment.invigilator_id && (<Button onClick={() => handleAcknowledge(assignment.id)} size="sm" className="ml-4 bg-blue-900 hover:bg-blue-800" data-testid="acknowledge-button"><CheckCircle className="mr-2 h-4 w-4" />Acknowledge</Button>)}
                    </div>
                  </Card>
                ))}
                </div>
              </div>
            )}
            <DateBucketedList
              items={restInvig}
              getDate={(a) => a.date}
              testidPrefix="invig-bucket"
              emptyLabel="No older invigilation duties."
              renderItem={(assignment) => (
              <Card key={assignment.id} className="dashboard-card p-6" data-testid="assignment-card">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900">{assignment.invigilator_name}</h3>
                      {assignment.is_acknowledged ? (
                        <span className="flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                          <CheckCircle className="mr-1 h-3 w-3" />
                          Acknowledged
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded">
                          Pending
                        </span>
                      )}
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-900 text-xs font-medium rounded">
                        {assignment.duty_type}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 text-sm text-slate-600">
                      <p><strong>Date:</strong> {assignment.date}</p>
                      <p><strong>Time:</strong> {assignment.time_slot}</p>
                      <p><strong>Venue:</strong> {assignment.venue}</p>
                    </div>
                  </div>
                  {!assignment.is_acknowledged && user?.id === assignment.invigilator_id && (
                    <Button onClick={() => handleAcknowledge(assignment.id)} size="sm" className="ml-4 bg-blue-900 hover:bg-blue-800" data-testid="acknowledge-button">
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Acknowledge
                    </Button>
                  )}
                </div>
              </Card>
              )}
            />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ─── Smart Draft Review Dialog ─── */}
      <Dialog open={draftDialogOpen} onOpenChange={(open) => { setDraftDialogOpen(open); if (!open) setDraft(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>Smart Invigilation Draft</DialogTitle>
          </DialogHeader>
          {draftLoading ? (
            <div className="flex items-center gap-2 py-8 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Generating draft...</div>
          ) : draft ? (
            <div className="space-y-4 mt-4" data-testid="draft-review-view">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{draft.exam?.exam_name} &mdash; {draft.exam?.subject}</p>
                    <p className="text-sm text-slate-500">{formatDateLong(draft.exam?.date)} &middot; {draft.exam?.start_time}-{draft.exam?.end_time} &middot; {draft.exam?.venue}</p>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full font-medium">{draft.assigned_count} / {draft.total_needed} assigned</span>
                    {draft.unassigned_slots > 0 && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full font-medium">{draft.unassigned_slots} unassigned</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900" data-testid="rules-applied-panel">
                <p className="font-semibold mb-1 flex items-center gap-1"><Info className="h-3 w-3" /> Rules applied</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {(draft.rules_applied || []).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="draft-slots-table">
                  <thead>
                    <tr className="border-b-2 border-slate-300">
                      <th className="p-2 text-left text-xs font-semibold text-slate-500">Role</th>
                      <th className="p-2 text-left text-xs font-semibold text-slate-500">Invigilator</th>
                      <th className="p-2 text-left text-xs font-semibold text-slate-500">Match</th>
                      <th className="p-2 text-left text-xs font-semibold text-slate-500">Workload (week / season)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.draft_slots.map((slot, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`draft-slot-row-${i}`}>
                        <td className="p-2 font-medium text-slate-800 capitalize">{slot.duty_type}</td>
                        <td className="p-2">
                          <Select value={slot.invigilator_id || ''} onValueChange={(v) => updateDraftSlot(i, v)}>
                            <SelectTrigger className={`h-8 text-xs ${slot.invigilator_id ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`} data-testid={`draft-slot-select-${i}`}>
                              <SelectValue placeholder="Unassigned" />
                            </SelectTrigger>
                            <SelectContent>
                              {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {slot.unassigned_reason && !slot.invigilator_id && (
                            <span className="text-[10px] text-red-500 mt-0.5 block">{slot.unassigned_reason}</span>
                          )}
                        </td>
                        <td className="p-2 text-xs text-slate-500">
                          {slot.match === 'subject_match' ? <span className="text-emerald-600 font-medium">Subject expert</span> : slot.match === 'any' ? 'Any' : '—'}
                        </td>
                        <td className="p-2 text-xs text-slate-500">{slot.workload_weekly} / {slot.workload_season}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <p className="text-xs text-slate-500">Review and change invigilators as needed, then confirm to save + notify.</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setDraftDialogOpen(false); setDraft(null); }}>Cancel</Button>
                  <Button onClick={handleConfirmDraft} disabled={confirmingDraft || !draft.draft_slots.some(s => s.invigilator_id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-draft-button">
                    {confirmingDraft ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserCheck className="mr-2 h-4 w-4" />}
                    {confirmingDraft ? 'Confirming...' : `Confirm & Notify (${draft.draft_slots.filter(s => s.invigilator_id).length})`}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ─── Bulk Import Dialog ─── */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Import Exam Schedule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBulkImport} className="space-y-4 mt-2">
            <div className="flex items-start justify-between gap-3 p-3 bg-sky-50 border border-sky-100 rounded-md">
              <div className="text-xs text-slate-600">
                <p className="font-semibold text-slate-800 mb-0.5">Not sure of the format?</p>
                <p>Download a pre-filled .xlsx template with example rows & column instructions.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-sky-300 text-sky-800 hover:bg-sky-100 shrink-0"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/exams/bulk-import-sample`, {
                      headers: { Authorization: `Bearer ${token}` },
                      responseType: 'blob',
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                    const a = document.createElement('a');
                    a.href = url; a.download = 'exam_schedule_sample.xlsx'; a.click();
                    window.URL.revokeObjectURL(url);
                  } catch {
                    toast.error('Failed to download sample');
                  }
                }}
                data-testid="download-sample-button"
              >
                <FileDown className="h-3.5 w-3.5 mr-1" /> Sample .xlsx
              </Button>
            </div>
            <p className="text-xs text-slate-500">Upload an Excel (.xlsx) or CSV with columns: <code>exam_name, date, start_time, end_time, subject, venue, num_students</code></p>
            <input ref={bulkFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setBulkFile(e.target.files[0])} data-testid="bulk-file-input" />
            {bulkFile && <p className="text-sm text-slate-600">Selected: {bulkFile.name}</p>}
            {bulkResult && (
              <div className="text-xs">
                <p className="text-emerald-700 font-medium">Created: {bulkResult.created}</p>
                {bulkResult.errors.length > 0 && (
                  <div className="text-red-700 mt-1">
                    Errors: <ul className="list-disc list-inside">{bulkResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}</ul>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)}>Close</Button>
              <Button type="submit" disabled={!bulkFile || bulkImporting} className="bg-blue-900 hover:bg-blue-800" data-testid="bulk-submit-button">
                {bulkImporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                {bulkImporting ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── Workload Stats Dialog ─── */}
      <Dialog open={workloadDialogOpen} onOpenChange={setWorkloadDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invigilation Workload (All-Time)</DialogTitle>
          </DialogHeader>
          {workloadLoading ? (
            <div className="flex items-center gap-2 py-4 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
          ) : workloadStats.length === 0 ? (
            <p className="text-sm text-slate-500 py-3">No invigilation assignments yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse" data-testid="workload-stats-table">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="p-2 text-left text-xs font-semibold text-slate-500">Teacher</th>
                    <th className="p-2 text-center text-xs font-semibold text-slate-500">Total</th>
                    <th className="p-2 text-center text-xs font-semibold text-slate-500">Main</th>
                    <th className="p-2 text-center text-xs font-semibold text-slate-500">Standby</th>
                    <th className="p-2 text-center text-xs font-semibold text-slate-500">Roaming</th>
                    <th className="p-2 text-center text-xs font-semibold text-slate-500">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {workloadStats.map(s => (
                    <tr key={s.invigilator_id} className="border-b border-slate-100">
                      <td className="p-2 font-medium text-slate-800">{s.invigilator_name}</td>
                      <td className="p-2 text-center font-semibold text-blue-700">{s.total}</td>
                      <td className="p-2 text-center">{s.main}</td>
                      <td className="p-2 text-center">{s.standby}</td>
                      <td className="p-2 text-center">{s.roaming}</td>
                      <td className="p-2 text-center">{s.unique_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Teacher: Submit Opt-Out Dialog ─── */}
      <Dialog open={optOutDialogOpen} onOpenChange={setOptOutDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Request Invigilation Opt-Out</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitOptOut} className="space-y-4 mt-2">
            {/* Mode picker */}
            <div className="grid grid-cols-3 gap-2" data-testid="optout-mode-picker">
              {[
                { key: 'single', label: 'Single Day' },
                { key: 'range', label: 'Date Range' },
                { key: 'multi', label: 'Multiple Days' },
              ].map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setOptOutForm(f => ({ ...f, mode: opt.key }))}
                  data-testid={`optout-mode-${opt.key}`}
                  className={`px-3 py-2 text-xs font-medium rounded-md border transition ${optOutForm.mode === opt.key ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >{opt.label}</button>
              ))}
            </div>

            {/* Single mode → date + optional time-frame */}
            {optOutForm.mode === 'single' && (
              <div className="space-y-3">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={optOutForm.date}
                    onChange={(e) => setOptOutForm({ ...optOutForm, date: e.target.value })}
                    data-testid="optout-single-date"
                  />
                </div>
                <div>
                  <Label className="flex items-center justify-between">
                    <span>Time-frame <span className="text-slate-400 font-normal">(optional)</span></span>
                    <span className="text-xs text-slate-400 font-normal">Leave blank for whole day</span>
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="time"
                      value={optOutForm.start_time}
                      onChange={(e) => setOptOutForm({ ...optOutForm, start_time: e.target.value })}
                      data-testid="optout-start-time"
                      className="flex-1"
                    />
                    <span className="text-slate-400">–</span>
                    <Input
                      type="time"
                      value={optOutForm.end_time}
                      onChange={(e) => setOptOutForm({ ...optOutForm, end_time: e.target.value })}
                      data-testid="optout-end-time"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Only invigilation duties whose time overlaps this window will be blocked.</p>
                </div>
              </div>
            )}

            {/* Range mode → start + end date */}
            {optOutForm.mode === 'range' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={optOutForm.start_date}
                    onChange={(e) => setOptOutForm({ ...optOutForm, start_date: e.target.value })}
                    data-testid="optout-start-date"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={optOutForm.end_date}
                    onChange={(e) => setOptOutForm({ ...optOutForm, end_date: e.target.value })}
                    data-testid="optout-end-date"
                  />
                </div>
              </div>
            )}

            {/* Multi mode → comma-separated dates */}
            {optOutForm.mode === 'multi' && (
              <div>
                <Label>Dates (comma-separated YYYY-MM-DD)</Label>
                <Input
                  value={optOutForm.dates}
                  onChange={(e) => setOptOutForm({ ...optOutForm, dates: e.target.value })}
                  placeholder="2026-06-01, 2026-06-02"
                  data-testid="optout-dates-input"
                />
              </div>
            )}

            <div>
              <Label>Reason</Label>
              <Textarea
                value={optOutForm.reason}
                onChange={(e) => setOptOutForm({ ...optOutForm, reason: e.target.value })}
                rows={3}
                placeholder="Medical appointment, personal matter, etc."
                data-testid="optout-reason-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOptOutDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-900 hover:bg-blue-800" data-testid="optout-submit-button">Submit Request</Button>
            </div>
          </form>
          {myOptOuts.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-500 mb-2">My Previous Requests</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {myOptOuts.map(o => (
                  <div key={o.id} className="flex justify-between items-center text-xs p-2 bg-slate-50 rounded">
                    <span>
                      {o.dates.join(', ')}
                      {(o.start_time || o.end_time) && (
                        <span className="ml-1 text-slate-500">
                          ({o.start_time || '00:00'}–{o.end_time || '23:59'})
                        </span>
                      )}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full font-medium ${o.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : o.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{o.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ─── Admin: Opt-Out Review Dialog ─── */}
      <Dialog open={optOutReviewDialogOpen} onOpenChange={setOptOutReviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invigilation Opt-Out Requests</DialogTitle>
          </DialogHeader>
          {allOptOuts.length === 0 ? (
            <p className="text-sm text-slate-500 py-3">No opt-out requests.</p>
          ) : (
            <div className="space-y-2">
              {allOptOuts.map(o => (
                <div key={o.id} className="border border-slate-200 rounded-md p-3" data-testid={`optout-row-${o.id}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{o.user_name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Dates: <strong>{o.dates.join(', ')}</strong>
                        {(o.start_time || o.end_time) && (
                          <span className="ml-1 text-slate-600">
                            · Time: <strong>{o.start_time || '00:00'}–{o.end_time || '23:59'}</strong>
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-600 mt-1">{o.reason}</p>
                      {o.reviewed_by && <p className="text-[10px] text-slate-400 mt-1">Reviewed by {o.reviewed_by} at {o.reviewed_at?.slice(0, 16)}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${o.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : o.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{o.status}</span>
                      {o.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => handleReviewOptOut(o.id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid={`optout-approve-${o.id}`}>Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => handleReviewOptOut(o.id, 'rejected')} className="h-7 text-xs border-red-300 text-red-700" data-testid={`optout-reject-${o.id}`}>Reject</Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExamInvigilationPage;